import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { resizeHighcharts } from 'components/dashboards/DashboardPage/DashboardGraph';
import GraphPlotList from 'components/dashboards/DashboardPage/GraphPlotList';
import { getGraphDataIds, LRQId } from 'components/dashboards/DashboardPage/DashboardRequestManager';
import DashboardChart from 'components/dashboards/DashboardPage/DashboardChart';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { showWaitCursor } from 'components/common/Page/Page';

import { Dashboard, GraphConfigType, GraphPlotConfigType } from 'components/dashboards/types';
import { createUpdatedGraphVariables, getPlots } from 'components/dashboards/dashboardData';
import { windowHeightState, windowWidthState } from 'components/common/Managers/LayoutStateManager';
import { queryTimeRangeState } from 'components/common/Managers/RouteStateManager.state';

import { useMutation } from '@apollo/client';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { getLrqStatus, LRQ_STATUS } from 'common/functions';
import ELEMENT from 'common/element';

import { plotsStylesState } from 'styles/stylesProvider';
import './DashboardViewGraph.scss';

import { EDIT_DASHBOARD_GRAPH, GET_CONFIGV2 } from '../queries';

type DashboardViewGraphProps = {
  dashboard: Dashboard;
  idList: LRQId[];
  graphIndex: number;
};

const DISPLAY_RATIO_HEIGHT = 5;
const DISPLAY_RATIO_WIDTH = 16;

/**
 * This component displays a full page view of a dashboard graph.
 *
 * @prop dashboard  The configuration, layout and data for this dashboard
 * @prop idList     The dataIds for the dashboard graphs
 * @prop graphIndex The index of the graph to show (within the config -- not necessarily the same as where it appears)
 */
function DashboardViewGraph({ dashboard, idList, graphIndex }: DashboardViewGraphProps): JSX.Element {
  const windowWidth = useRecoilValue(windowWidthState);
  const windowHeight = useRecoilValue(windowHeightState);
  const [highChart, setHighChart] = useState<Highcharts.Chart>();
  const plotsStyles = useRecoilValue(plotsStylesState);

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const [editDashboardGraph] = useMutation(EDIT_DASHBOARD_GRAPH, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboard.config.name, startTime, endTime } }],
  });
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const dataIds = getGraphDataIds(idList, graphIndex);
  const { results, errors } = useEventCache(dataIds);
  const lrqStatus = getLrqStatus(results, errors, true);
  const noResults = lrqStatus !== LRQ_STATUS.DATA_READY;

  if (lrqStatus === LRQ_STATUS.DATA_READY || lrqStatus === LRQ_STATUS.HAS_ERRORS) {
    showWaitCursor(false);
  }

  const configs = dashboard.config.graphs as GraphConfigType[];
  const graphConfig = dashboard.config.graphs[graphIndex] as GraphConfigType;
  const graphPlots = graphConfig.plots as GraphPlotConfigType[];
  const plots = noResults ? [] : getPlots(graphConfig, results, plotsStyles, dashboard.params, true);

  useEffect(() => {
    if (highChart) {
      resizeHighcharts(highChart);
    }
  }, [highChart, windowWidth, windowHeight]);
  const afterChartCreated = (_index: number, newChart: Highcharts.Chart): void => setHighChart(newChart);

  const mutateGraph = (updatePlotConfigs: GraphPlotConfigType[]): void => {
    const variables = createUpdatedGraphVariables(
      dashboard.config.name,
      graphConfig,
      graphIndex,
      updatePlotConfigs,
      dashboard?.config?.tabName,
    );
    editDashboardGraph({ variables }).catch((error) => {
      setPageAlertConfig({ message: error.message, variant: PageAlertVariant.ERROR, autoHide: true });
    });
  };

  const addPlotCallback = (plotConfig: GraphPlotConfigType): void => {
    const plotsCopy = [...graphPlots];
    plotsCopy.push(plotConfig);
    mutateGraph(plotsCopy);
  };

  const editPlotCallback = (plotIndex: number, plotConfig: GraphPlotConfigType): void => {
    const plotsCopy = [...graphPlots];
    plotsCopy[plotIndex] = plotConfig;
    mutateGraph(plotsCopy);
  };

  const showHidePlot = (index: number, show: boolean): void => {
    if (highChart) {
      const series = highChart.series;
      if (show) {
        series[index].show();
      } else {
        series[index].hide();
      }
    }
  };

  const properGraphHeight = Math.floor((windowWidth * DISPLAY_RATIO_HEIGHT) / DISPLAY_RATIO_WIDTH);
  const windowGraphHeight = Math.floor(windowHeight * 0.4);
  const graphHeight = Math.min(properGraphHeight, windowGraphHeight);

  return (
    <div className="DashboardViewGraph" data-test-id={ELEMENT.dashboardViewGraph}>
      <div className="DashboardViewGraph__Chart" style={{ height: graphHeight }}>
        <DashboardChart
          params={dashboard.params}
          windowWidth={windowWidth}
          index={graphIndex}
          config={configs[graphIndex]}
          dataIds={dataIds}
          chartInstanceCallback={afterChartCreated}
        />
      </div>
      <GraphPlotList
        config={graphConfig}
        plotsToDisplay={plots as GraphPlotConfigType[]}
        fullPage
        plotData={plots}
        showHideCallback={showHidePlot}
        addPlotCallback={addPlotCallback}
        editPlotCallback={editPlotCallback}
      />
    </div>
  );
}

export default DashboardViewGraph;
